import { format } from "date-fns";
import { Icon, Tooltip, Typography } from "@/components/atoms";
import { FlightStatus } from "@/redux/apis/clientFlight/types";
import { FlightStatusBadge } from "./FlightStatusBadge";
import { formatFlightDate, formatFlightTime } from "./flightPickup";

/**
 * Why a pickup is holding while the gate time beside it has moved — the engine's own freeze
 * (`flight_pickup_frozen_reason`), read from the store rather than inferred from the flight's status: a
 * `landed` status does not mean the pickup is pinned, and a pinned one is not always landed.
 *
 * These three, and only these three, because they are the states the design covers. A pickup held back by
 * `validatePickup` — the re-derived time has passed, or falls inside the backend's lead — holds SILENTLY:
 * the guard is unchanged and still protects the booking, but explaining it would mean inventing copy the
 * design does not have, and the lead itself is a backend threshold nobody agreed to state to a client.
 */
export type PickupHoldReason = "at_gate" | "cancelled" | "diverted";

/**
 * What the card needs to explain the pickup, sourced whole from the panel that owns the derivation.
 *
 * Every field is already computed there; nothing in this file recomputes a time or a decision. That is the
 * point of passing a struct rather than a flight and a wait: a footer that rebuilt `landing + delay` for
 * itself would be a second derivation to keep in step with the one the sheet actually commits, and the
 * bookings where they diverge are exactly the ones this disclosure exists for.
 */
export interface FlightPickupDisclosure {
  /**
   * The pickup as COMMITTED on the draft. A `Date` in the commit convention — its BROWSER-LOCAL fields
   * hold the booking location's wall clock — so it is formatted with plain `date-fns`, never the
   * `formatFlightTime` family, which reads an offset off an ISO and is for airport-local flight times.
   * Same reasoning, same hazard, as `DriverArrivesCard`.
   */
  pickupAt: Date;
  /**
   * Where the pickup LANDS if this edit is saved, or null when it is not moving.
   *
   * Read from the same `resolvePickup` call `DriverArrivesCard` is given, so the arrow in the pickup row
   * and the card below it cannot state different times. Deriving it here from the live arrival and the wait
   * would look identical and be wrong on the anchored branch, which hands a booked pickup back untouched
   * rather than rebuilding it.
   */
  proposedPickupAt: Date | null;
  /**
   * The gate arrival the committed pickup was derived from, already carrying the arrival airport's offset.
   * Null on the create path, where there is no booked pickup and so nothing to compare against.
   */
  bookedGateIso: string | null;
  /** The gate arrival the flight is showing NOW, same offset convention. */
  liveGateIso: string;
  /**
   * Whether the sheet will move the pickup onto {@link liveGateIso}. The panel's own answer, not a
   * threshold applied here — since DEV-1531 the client edit path re-derives on ANY drift, so this is the
   * only thing that knows.
   */
  pickupMoves: boolean;
  /**
   * WHY the pickup is not following the gate time, when it is not. Null when it is following, or when it is
   * holding for no reason worth stating.
   *
   * ONE field, deliberately, so exactly one note can ever render — two stacked explanations is the
   * over-wording this footer has already been pulled back from once. The panel decides which reason wins;
   * this file only knows how to say them.
   */
  holdReason: PickupHoldReason | null;
  status: FlightStatus;
  /** Minutes late (positive) or early (negative) — what turns `scheduled` into a "Flight Early" badge. */
  delay: number;
}

/**
 * The pickup follows the flight on EVERY change (DEV-1875 silent rewrites); 15/30 is now only the size of
 * move that earns an email, close to pickup. `flightUpdateIsMaterial` remains the statement of that notify
 * threshold.
 */
const PICKUP_FOLLOWS_FLIGHT_TOOLTIP =
  "We keep your pickup in step with your flight and email you if it moves 15 min or more (30 international) close to pickup.";

/** Live gate minus booked gate, in whole minutes — negative when the flight is now arriving EARLIER. */
const driftSigned = (bookedIso: string, liveIso: string): number => Math.round((Date.parse(liveIso) - Date.parse(bookedIso)) / 60_000);

/** `-7 min` / `+12 min`, as the design prints it beside the live gate time. */
const driftLabel = (minutes: number): string => `${minutes > 0 ? "+" : "-"}${Math.abs(minutes)} min`;

/**
 * A gate time for the two-row branch: the clock, plus the DATE when the two arrivals fall on different
 * days. Without it a flight moved exactly 24 hours prints the same clock twice with `+1440 min` beside it,
 * which reads as a typo rather than as a day.
 */
const gateValue = (iso: string, withDate: boolean): string =>
  withDate ? `${formatFlightTime(iso)}, ${formatFlightDate(iso)}` : formatFlightTime(iso);

/**
 * Has the gate time actually MOVED since the booking was made?
 *
 * The whole disclosure hangs off this, so it is exported rather than kept private: `FlightDetailsCard` asks
 * it to decide whether to render the footer at all, and the footer asks it for nothing else — computing it
 * twice is how a card ends up showing a drift block that says there is no drift.
 *
 * Judged on RENDERED WALL CLOCKS, not instants, because that is what the reader compares: two arrivals
 * forty seconds apart both read "6:45 AM", and a footer explaining the gap between them explains nothing.
 * Two refinements the raw clock comparison misses, in opposite directions — a different DAY is drift even
 * at an identical clock, and a gap that rounds to zero is not drift even across a minute boundary.
 */
export const hasGateDrift = ({ bookedGateIso, liveGateIso }: Pick<FlightPickupDisclosure, "bookedGateIso" | "liveGateIso">): boolean => {
  if (!bookedGateIso) return false;
  if (driftSigned(bookedGateIso, liveGateIso) === 0) return false;
  return (
    formatFlightDate(bookedGateIso) !== formatFlightDate(liveGateIso) || formatFlightTime(bookedGateIso) !== formatFlightTime(liveGateIso)
  );
};

/**
 * The "did NOT move" half of DSN-641, which had no surface at all until J000551 showed a gap acknowledged
 * and left unexplained.
 *
 * Exhaustive with no `default`, so a new {@link PickupHoldReason} is a compile error here rather than a
 * silently unexplained hold. `cancelled` and `diverted` have their own footer on this card and cannot reach
 * here in practice; they are spelled out anyway, because a reader who does reach them is owed the reason.
 */
const holdCopy = (reason: PickupHoldReason, pickupClock: string): string => {
  switch (reason) {
    case "at_gate":
      return `This flight has arrived, so the pickup stays at ${pickupClock}.`;
    case "cancelled":
      return `This flight is cancelled, so the pickup stays at ${pickupClock}.`;
    case "diverted":
      return `This flight was diverted, so the pickup stays at ${pickupClock}.`;
  }
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center gap-2">
    <Typography variant="muted" className="text-neutral-dark-gray">
      {label}
    </Typography>
    <Typography variant="action" className="text-neutral-dark-gray">
      {value}
    </Typography>
  </div>
);

/**
 * The flight card's footer (DSN-641, RM-Client 7039-22276), in the place `Lands 1:25 PM • Terminal 1` used
 * to hold.
 *
 * On booking J389084 the sheet showed a 6:05 AM pickup and a 6:18 AM flight card with nothing between
 * them, and an operator escalated to two people to be told the booking was correct. Everything needed to
 * answer that was already resolved and simply never rendered: the pickup, the arrival it was built from,
 * the arrival the flight is showing now, and whether the sheet is about to move one onto the other.
 *
 * ## Two gate times, or one
 *
 * "Gate Time" on its own was the defect, not the fix — the reader's question is precisely WHICH gate time,
 * and a single unlabelled one re-poses it. So the gate row carries BOTH arrivals, each qualified inline,
 * with the signed gap between them.
 *
 * There is no single-arrival variant, because there is no reason to render this block without drift at all:
 * `Pickup 5:22 PM / Gate Time 5:22 PM` is one fact stated twice. The caller gates on {@link hasGateDrift}
 * and falls back to the card's ordinary `Lands … • Terminal …` footer, which is also what puts the terminal
 * back on screen for every booking that has nothing to explain.
 *
 * ## The pickup row states the change AS a change
 *
 * `9:25 PM → 9:18 PM`, rather than the booked time here and the proposed one in `DriverArrivesCard` two
 * boxes below with a delay input between them. That split is a softer version of the bug this footer
 * exists to fix: both numbers on screen, correct and labelled, and the reader still has to diff two boxes
 * to learn a change is happening at all.
 *
 * The design's hidden "pickup stays" variant (7039:22292) is deliberately NOT built. It explained a hold
 * by the engine's 15/30 materiality rule, and this path applies no threshold — so the note was unreachable
 * wherever its reason was true, and false wherever it rendered.
 */
export const FlightPickupFooter = ({ disclosure }: { disclosure: FlightPickupDisclosure }) => {
  const { pickupAt, proposedPickupAt, bookedGateIso, liveGateIso, pickupMoves, holdReason, status, delay } = disclosure;

  // Non-null by the caller's `hasGateDrift` gate, which is the condition for rendering this at all.
  const gapMinutes = driftSigned(bookedGateIso!, liveGateIso);
  const differentDay = formatFlightDate(bookedGateIso!) !== formatFlightDate(liveGateIso);
  // The arrow is earned by the pickup actually landing somewhere else, not merely by the sheet intending to
  // re-derive: a re-derivation onto the same wall clock is not a change the reader needs to be shown.
  // Dated exactly when the move crosses midnight, the same way the gate row dates its two arrivals. An
  // arrival pulled earlier can drag the pickup back over midnight (a 00:20 booked pickup rebuilt onto a
  // 23:50 one), and `10:06 PM → 10:35 PM` across two days is a different claim from the same-day reading.
  const pickupCrossesDay = !!proposedPickupAt && format(pickupAt, "yyyy-MM-dd") !== format(proposedPickupAt, "yyyy-MM-dd");
  const pickupClock = (at: Date) => format(at, pickupCrossesDay ? "h:mm a, d MMM yyyy" : "h:mm a");
  const bookedPickupClock = pickupClock(pickupAt);
  const proposedPickupClock = proposedPickupAt ? pickupClock(proposedPickupAt) : null;
  const pickupChanges = pickupMoves && !!proposedPickupClock && proposedPickupClock !== bookedPickupClock;
  // Only where the pickup is actually holding. A frozen booking the client has since re-dated or re-waited
  // still moves its pickup from this panel, and "the pickup stays at 5:22" above an arrow to 5:23 would be
  // the contradiction this footer exists to remove.
  const note = pickupChanges || !holdReason ? null : holdCopy(holdReason, bookedPickupClock);

  return (
    <div className="flex flex-col gap-2 border-t border-neutral-mid-gray bg-neutral-surface-gray p-3">
      {pickupChanges ? (
        <div className="flex items-center gap-2">
          <Typography variant="muted" className="text-neutral-dark-gray">
            Pickup Time
          </Typography>
          <Typography variant="action" className="text-neutral-dark-gray">
            {/* The arrow carries the whole claim visually and reads as punctuation aloud, so the sentence
                is spoken and the glyph is hidden rather than left to a screen reader to interpret. */}
            <span className="sr-only">{`Moving from ${bookedPickupClock} to ${proposedPickupClock}`}</span>
            <span aria-hidden>{`${bookedPickupClock} → ${proposedPickupClock}`}</span>
          </Typography>
        </div>
      ) : (
        <Row label="Pickup Time" value={bookedPickupClock} />
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Typography variant="muted" className="text-neutral-dark-gray">
          Gate Time
        </Typography>
        {/* Both arrivals on ONE row, each qualified inline rather than split across two labelled rows, where
            "booked" would sit a line under the pickup meaning a different thing — when the PLANE was
            expected against when the CAR comes.
            Each qualifier and its time are one nested flex item, so the wrapping row breaks BETWEEN the
            pairs and never inside one: a line ending in "booked" with its time orphaned below reads as a
            different claim. */}
        <span className="flex flex-nowrap items-center gap-2 whitespace-nowrap">
          <Typography variant="muted" className="text-neutral-dark-gray">
            booked
          </Typography>
          <Typography variant="action" className="text-neutral-dark-gray">
            {gateValue(bookedGateIso!, differentDay)}
          </Typography>
        </span>
        <span aria-hidden className="size-[5px] shrink-0 rounded-full bg-neutral-mid-gray" />
        <span className="flex flex-nowrap items-center gap-2 whitespace-nowrap">
          <Typography variant="muted" className="text-neutral-dark-gray">
            live
          </Typography>
          <Typography variant="action" className="text-neutral-dark-gray">
            {gateValue(liveGateIso, differentDay)}
          </Typography>
        </span>
        <span aria-hidden className="size-[5px] shrink-0 rounded-full bg-neutral-mid-gray" />
        <Typography variant="muted" className="text-neutral-dark-gray">
          {driftLabel(gapMinutes!)}
        </Typography>
        {/* ONE help icon. The design node carries two 20px help-circles side by side (7039:22286 and
            7039:22287) but supplies a single tooltip, whose tail lands on the second — the frame is showing
            the resting and hovered states of one control, not two controls. */}
        <Tooltip content={PICKUP_FOLLOWS_FLIGHT_TOOLTIP} maxWidth={280}>
          <Icon
            name="help-circle"
            isCustom
            variant="Outline"
            size="md"
            role="img"
            aria-label={PICKUP_FOLLOWS_FLIGHT_TOOLTIP}
            className="shrink-0 text-neutral-silver-gray"
          />
        </Tooltip>
      </div>

      <FlightStatusBadge status={status} delay={delay} />

      {/* One line, in the slot the removed materiality note used. That note needed two lines because it was
          explaining a rule; this states a fact the booking already carries. */}
      {note && (
        <Typography variant="caption" className="block text-neutral-dark-gray">
          {note}
        </Typography>
      )}
    </div>
  );
};
